import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { z } from "zod"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { NameFilter } from "@/components/layout/name-filter"
import { wasUpdated } from "@/routes/_layout/settings/_panel/-lib/history"
import { PersonFilter } from "../../-components/person-filter"
import { PlaybookBar } from "../-components/playbook-bar"
import { RuleTable } from "../-components/rule-table"
import {
  RULE_GROUPING_LABELS,
  RULE_GROUPINGS,
  type RuleFilters,
  type RuleGrouping,
  ruleGroupingSchema,
  updater,
  useRuleRows,
} from "../-lib/rules"

const searchSchema = z.object({
  search: z.string().optional().catch(undefined),
  creator: z.string().optional().catch(undefined),
  updater: z.string().optional().catch(undefined),
  group: ruleGroupingSchema.optional().catch(undefined),
  subgroup: ruleGroupingSchema.optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/quoting/playbooks/$playbookId/rules",
)({
  validateSearch: (search) => searchSchema.parse(search),
  component: RulesPage,
})

function RulesPage(): React.ReactElement {
  const { playbookId } = Route.useParams()
  const { group, subgroup, ...filters } = Route.useSearch()
  const [drafting, setDrafting] = useState(false)
  const display = {
    group,
    subgroup: group && subgroup !== group ? subgroup : undefined,
  }

  return (
    <div>
      <PlaybookBar playbookId={playbookId}>
        <Toolbar
          playbookId={playbookId}
          filters={filters}
          display={display}
          onNewRule={() => setDrafting(true)}
        />
      </PlaybookBar>
      <RuleTable
        playbookId={playbookId}
        filters={filters}
        display={display}
        drafting={drafting}
        onDraftClose={() => setDrafting(false)}
      />
      <Outlet />
    </div>
  )
}

function Toolbar({
  playbookId,
  filters,
  display,
  onNewRule,
}: {
  playbookId: string
  filters: RuleFilters
  display: Display<RuleGrouping>
  onNewRule: () => void
}): React.ReactElement {
  const navigate = useNavigate()
  const rows = useRuleRows(playbookId)
  const canEdit = useHasPermission("quote_ruleset:update")
  const apply = (patch: Partial<RuleFilters & Display<RuleGrouping>>) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })

  return (
    <>
      <NameFilter
        search={filters.search ?? ""}
        onSearch={(next) => apply({ search: next || undefined })}
        placeholder="Search rules…"
      />
      <PersonFilter
        sections={[
          {
            label: "Created by",
            people: rows.flatMap(({ record }) => record.created_by ?? []),
            total: rows.length,
            selected: filters.creator,
            onChange: (creator) => apply({ creator }),
          },
          {
            label: "Updated by",
            people: rows.flatMap((row) => updater(row) ?? []),
            total: rows.filter(({ record }) => wasUpdated(record)).length,
            selected: filters.updater,
            onChange: (next) => apply({ updater: next }),
          },
        ]}
      />
      <DisplayOptions
        groupings={RULE_GROUPINGS}
        labels={RULE_GROUPING_LABELS}
        {...display}
        onChange={apply}
      />
      {canEdit && (
        <Button size="sm" onClick={onNewRule}>
          New rule
        </Button>
      )}
    </>
  )
}
